import { apiClient } from './client'
import type { CreateTenantEligibilityOverlayRequest, TenantEligibilityOverlayView } from '../types/eligibilityOverlay'

/**
 * Typed fetch wrappers for `module.eligibility`'s tenant maker-checker overlay surface (IB-288,
 * FR-M7.6, ADR-0056, IB-289 UI). Thin wrapper convention, same as `api/cartridgeStudio.ts` /
 * `api/aiCartridgeDraft.ts`.
 *
 * <p><b>Deliberately NOT exported (named gaps, not invented endpoints — see the story report):</b>
 * a `listTenantEligibilityOverlays(baseModelId)` and a `getResolutionPreview(id, asOf)` would both
 * be reasonable, but the merged `TenantEligibilityOverlayController` (IB-288) exposes neither — only
 * `create`/`get`/`approve`. Building either here would mean calling a URL the backend 404s on. Filed
 * as an IB-288 follow-up (see `docs/design/ib-289-tenant-eligibility-overlay-ui-design.md` §5/§9);
 * the screens that would have used them (`EligibilityOverlaysListScreen`/`OverlayResolutionPreview`)
 * are scoped down accordingly.
 *
 * GUARDED-WRITE CONTRACT: this file never authors a guarded field. An overlay changes WHICH
 * table/gate decisions fire (JSONB delta content only) — never `qre_pct`/`qualifying_pct`/a credit
 * figure — so nothing here routes through, mimics, or bypasses `AdjustmentService`.
 */

export async function createTenantEligibilityOverlay(
  request: CreateTenantEligibilityOverlayRequest,
): Promise<TenantEligibilityOverlayView> {
  const { data } = await apiClient.post<TenantEligibilityOverlayView>('/tenant-eligibility-overlays', request)
  return data
}

export async function getTenantEligibilityOverlay(
  id: string,
  signal?: AbortSignal,
): Promise<TenantEligibilityOverlayView> {
  const { data } = await apiClient.get<TenantEligibilityOverlayView>(
    `/tenant-eligibility-overlays/${encodeURIComponent(id)}`,
    { signal },
  )
  return data
}

/** Maker≠checker is enforced server-side ONLY (`TenantEligibilityOverlayService.approve` — 403 on
 * self-approval, even if the actor also holds `eligibility_overlay:A`). This call never pre-decides
 * whether approval will succeed — see `OverlayApproveBar`'s doc comment for the disclosed
 * "cannot pre-disable, the 403 is the enforcement" gap. */
export async function approveTenantEligibilityOverlay(id: string): Promise<TenantEligibilityOverlayView> {
  const { data } = await apiClient.post<TenantEligibilityOverlayView>(
    `/tenant-eligibility-overlays/${encodeURIComponent(id)}/approve`,
    {},
  )
  return data
}
